export interface PersistentStore<T> {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => T;
  set: (next: T | ((prev: T) => T)) => void;
  clear: () => void;
}

export function createPersistentStore<T>(
  initialValue: T,
  storageKey?: string
): PersistentStore<T> {
  let state: T = initialValue;
  const listeners = new Set<() => void>();

  if (storageKey && typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw !== null) {
        state = JSON.parse(raw);
      }
    } catch {
      // Fallback to in-memory initial value
    }
  }

  const notify = () => {
    listeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        console.error('Store listener error:', err);
      }
    });
  };

  const persist = (val: T) => {
    if (storageKey && typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(val));
      } catch {
        // Silently tolerate storage full or disabled
      }
    }
  };

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return state;
    },
    set(next: T | ((prev: T) => T)) {
      const nextVal = typeof next === 'function' ? (next as (prev: T) => T)(state) : next;
      if (nextVal === state) return;
      state = nextVal;
      persist(state);
      notify();
    },
    clear() {
      state = initialValue;
      if (storageKey && typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          // ignore
        }
      }
      notify();
    },
  };
}
